<template>
  <div class="admin-login-container" @click="createRipple">
    <!-- 背景图片 -->
    <div class="login-background">
      <img src="@/assets/background.png" alt="背景" class="background-image" />
    </div>

    <!-- 登录内容 -->
    <div class="login-content">
      <div class="login-card">
        <div class="login-header">
          <div class="login-logo">
            <i class="el-icon-user-solid"></i>
          </div>
          <h1 class="login-title">管理员登录</h1>
          <p class="login-subtitle">请输入您的管理员账号信息</p>
          <div class="login-divider"></div>
        </div>

        <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          autocomplete="off"
          @submit.native.prevent="handleLogin"
        >
          <!-- 密码登录模式 -->
          <template v-if="!showCodeLogin">
            <el-form-item prop="username">
              <div class="input-with-label">
                <label class="input-label">用户名:</label>
                <el-input
                  v-model="loginForm.username"
                  placeholder="请输入用户名或手机号"
                  prefix-icon="el-icon-user"
                  size="large"
                  autocomplete="off"
                />
              </div>
            </el-form-item>

            <el-form-item prop="password">
              <div class="input-with-label">
                <label class="input-label">密码:</label>
                <el-input
                  v-model="loginForm.password"
                  type="password"
                  placeholder="请输入密码"
                  prefix-icon="el-icon-lock"
                  size="large"
                  show-password
                  autocomplete="new-password"
                  @keyup.enter.native="handleLogin"
                />
              </div>
            </el-form-item>
          </template>

          <!-- 验证码登录模式 -->
          <template v-else>
            <el-form-item prop="mobile">
              <div class="input-with-label">
                <label class="input-label">手机号</label>
                <el-input
                  v-model="loginForm.mobile"
                  placeholder="请输入手机号"
                  prefix-icon="el-icon-mobile-phone"
                  size="large"
                  autocomplete="off"
                />
              </div>
            </el-form-item>

            <el-form-item prop="code">
              <div class="input-with-label">
                <label class="input-label">验证码</label>
                <div class="code-input-group">
                  <el-input
                    v-model="loginForm.code"
                    placeholder="请输入验证码"
                    prefix-icon="el-icon-message"
                    size="large"
                    autocomplete="off"
                  />
                  <el-button 
                    type="primary" 
                    size="large"
                    :disabled="codeCountdown > 0"
                    @click="sendCode"
                    class="code-button"
                  >
                    {{ codeCountdown > 0 ? `${codeCountdown}s` : '获取验证码' }}
                  </el-button>
                </div>
              </div>
            </el-form-item>
          </template>

          <el-form-item>
            <el-button 
              type="primary" 
              size="large" 
              class="login-button"
              :loading="loading"
              @click="handleLogin"
            >
              <i class="el-icon-right" v-if="!loading"></i>
              {{ loading ? '登录中...' : '登录' }}
            </el-button>
          </el-form-item>

          <div class="login-tips">
            <div class="tip-item">
              <i class="el-icon-info"></i>
              <span>请确保使用正确的管理员账号</span>
            </div>
            <div class="tip-item">
              <i class="el-icon-lock"></i>
              <span>密码登录和验证码登录均可使用</span>
            </div>
            <div class="tip-item">
              <i class="el-icon-warning"></i>
              <span>需要文件管理权限以上才能进入管理系统</span>
            </div>
          </div>

          <div class="login-actions">
            <el-button 
              type="text" 
              @click="toggleLoginMode"
              class="switch-button"
            >
              {{ showCodeLogin ? '密码登录' : '验证码登录' }}
            </el-button>
            <el-button 
              type="text" 
              @click="goBack"
              class="back-button"
            >
              <i class="el-icon-arrow-left"></i>
              返回首页
            </el-button>
          </div>
        </el-form>
      </div>
    </div>

    <!-- 点击波纹效果 -->
    <div 
      v-for="ripple in ripples" 
      :key="ripple.id"
      class="ripple-effect"
      :style="ripple.style"
    ></div>
  </div>
</template>

<script>
import api from '@/api/api';
import { 
  createAdminSession, 
  clearAdminData, 
  isAdminLoggedIn 
} from '@/utils/adminAuth';
import { clearOtherLoginStorage } from '@/utils/storageUtils';

export default {
  name: 'AdminLogin',
  data() {
    return {
      loading: false,
      showCodeLogin: false,
      codeCountdown: 0,
      ripples: [],
      loginForm: {
        username: '',
        password: '',
        mobile: '',
        code: ''
      },
      loginRules: {
        username: [
          { required: true, message: '请输入用户名或手机号', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ],
        mobile: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { pattern: /^\d{4}$/, message: '请输入4位数字验证码', trigger: 'blur' }
        ]
      }
    };
  },
  methods: {
    // 检查管理员权限 - 权限向上兼容版本
    checkAdminPermission(permissions) {
      // 有文件管理、系统管理或用户管理权限都可以进入
      return permissions.canFileManage || permissions.canUserManage || permissions.canSystemManage;
    },

    // 获取权限描述 - 权限向上兼容版本
    getPermissionDescription(permissions) {
      if (permissions.canUserManage) return '用户管理员（最高权限）';
      if (permissions.canSystemManage) return '系统管理员（分类管理以上）';
      if (permissions.canFileManage) return '文件管理员（文件管理）';
      return '普通用户';
    },

    // 登录成功后根据权限跳转 - 权限向上兼容版本
    redirectAfterLogin(permissions) {
      // 根据权限层级跳转：用户管理 > 系统管理 > 文件管理
      if (permissions.canUserManage) {
        // 用户管理员可以管理所有，跳转到文件管理页面
        this.$router.push({ name: 'FileManagement' });
      } else if (permissions.canSystemManage) {
        // 系统管理员可以管理分类，跳转到分类管理页面
        this.$router.push({ name: 'CategoryManagement' });
      } else if (permissions.canFileManage) {
        // 文件管理员只能管理文件，跳转到文件管理页面
        this.$router.push({ name: 'FileManagement' });
      } else {
        // 其他情况跳转到个人中心
        this.$router.push({ name: 'Personalcenter' });
      }
    },

    // 创建点击波纹效果
    createRipple(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;
      
      const ripple = {
        id: Date.now(),
        style: {
          width: size + 'px',
          height: size + 'px',
          left: x + 'px',
          top: y + 'px'
        }
      };
      
      this.ripples.push(ripple);
      
      setTimeout(() => {
        const index = this.ripples.findIndex(r => r.id === ripple.id);
        if (index > -1) {
          this.ripples.splice(index, 1);
        }
      }, 600);
    },


    // 切换登录模式
    toggleLoginMode() {
      this.showCodeLogin = !this.showCodeLogin;
      this.resetForm();
    },

    // 重置表单
    resetForm() {
      this.loginForm = {
        username: '',
        password: '',
        mobile: '',
        code: ''
      };
      this.$nextTick(() => {
        this.$refs.loginForm.clearValidate();
      });
    },

    // 发送验证码
    async sendCode() {
      if (!this.loginForm.mobile) {
        this.$message.warning('请先输入手机号');
        return;
      }

      try {
        await api.sendAdminLoginCode({
          mobile: this.loginForm.mobile
        });
        this.$message.success('验证码已发送');
        this.startCountdown();
      } catch (error) {
        this.$message.error('发送验证码失败，请重试');
      }
    },

    // 开始倒计时
    startCountdown() {
      this.codeCountdown = 60;
      const timer = setInterval(() => {
        this.codeCountdown--;
        if (this.codeCountdown <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    },

    // 处理登录
    async handleLogin() {
      try {
        await this.$refs.loginForm.validate();
      } catch (error) {
        return;
      }

      this.loading = true;

      try {
        let response;
        if (this.showCodeLogin) {
          response = await api.adminLoginByCode({
            mobile: this.loginForm.mobile,
            code: this.loginForm.code
          });
        } else {
          response = await api.adminLogin({
            username: this.loginForm.username,
            password: this.loginForm.password
          });
        }

        if (response.data && response.data.code === 200) {
          const loginData = response.data.data;
          const { token, jwtToken, ...userInfo } = loginData;
          
          // 提取权限信息
          const permissions = {
            canFileManage: userInfo.canFileManage || false,
            canUserManage: userInfo.canUserManage || false,
            canSystemManage: userInfo.canSystemManage || false,
            role: userInfo.role || '',
            permissionLevel: userInfo.permissionLevel || 0
          };

          // 检查权限：必须有文件管理权限以上才能进入
          const hasRequiredPermission = this.checkAdminPermission(permissions);

          if (!hasRequiredPermission) {
            const permissionDesc = this.getPermissionDescription(permissions);
            this.$message.error(`权限不足，${permissionDesc}无法进入管理系统`);
            this.loading = false;
            return;
          }

          // 登录前清除普通用户存储，确保登录方式隔离
          clearOtherLoginStorage('admin');

          // 创建管理员会话（使用localStorage存储，支持新旧两种token）
          this.createAdminSessionInLocalStorage(token, userInfo, permissions, jwtToken);

          // 同步管理员状态到Vuex store
          this.$store.dispatch("token", {
            value: token,
            remember: true
          });
          this.$store.dispatch("account", {
            value: userInfo.account || userInfo.username || '',
            remember: true
          });
          this.$store.dispatch("avatar", {
            value: userInfo.avatar || '',
            remember: true
          });
          this.$store.dispatch("userName", {
            value: userInfo.name || userInfo.username || '',
            remember: true
          });
          this.$store.dispatch("vip", {
            value: userInfo.vip || false,
            remember: true
          });
          this.$store.dispatch("vipName", {
            value: userInfo.vipName || '',
            remember: true
          });
          this.$store.dispatch("vipDate", {
            value: userInfo.vipDate || '',
            remember: true
          });
          
          // 更新权限信息到store
          this.$store.state.canFileManage = permissions.canFileManage;
          this.$store.state.canUserManage = permissions.canUserManage;
          this.$store.state.canSystemManage = permissions.canSystemManage;
          this.$store.state.role = permissions.role;
          this.$store.state.permissionLevel = permissions.permissionLevel;
          this.$store.state.userInfo = userInfo;

          this.$message.success('管理员登录成功');
          
          // 根据权限跳转到相应页面
          this.redirectAfterLogin(permissions);
        } else {
          this.$message.error(response.data?.msg || '登录失败');
        }
      } catch (error) {
        this.$message.error('登录失败，请重试');
      } finally {
        this.loading = false;
      }
    },


    // 在localStorage中创建admin会话
    createAdminSessionInLocalStorage(token, userInfo, permissions, jwtToken = null) {
      // 存储admin token到localStorage
      localStorage.setItem('admin_token', token);
      const expiryTime = new Date().getTime() + 24 * 60 * 60 * 1000;
      localStorage.setItem('admin_token_expiry', expiryTime.toString());
      
      // 存储admin JWT token到localStorage（如果存在）
      if (jwtToken) {
        localStorage.setItem('admin_token_jwt', jwtToken);
        localStorage.setItem('admin_token_jwt_expiry', expiryTime.toString());
      }
      
      // 存储admin用户信息到localStorage
      localStorage.setItem('admin_user_info', JSON.stringify(userInfo));
      
      // 存储admin权限信息到localStorage
      localStorage.setItem('admin_permissions', JSON.stringify(permissions));
      
      // 设置admin会话标识
      localStorage.setItem('admin_session', 'active');
    },

    // 返回首页
    goBack() {
      this.$router.push({ name: 'indexPage' });
    }
  }
};
</script>

<style scoped>
.admin-login-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  cursor: pointer;
}

.login-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.login-content {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 6px;
  padding: 30px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 420px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.login-header {
  text-align: center;
  margin-bottom: 25px;
}

.login-logo {
  width: 60px;
  height: 60px;
  background: #409eff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 15px auto;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.login-logo i {
  font-size: 28px;
  color: white;
}

.login-title {
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px 0;
}

.login-subtitle {
  color: #666;
  font-size: 14px;
  margin: 0 0 15px 0;
}

.login-divider {
  width: 60px;
  height: 3px;
  background: #409eff;
  border-radius: 2px;
  margin: 0 auto;
}

.login-form {
  margin-top: 10px;
}

.login-form .el-form-item {
  margin-bottom: 16px;
}

.code-input-group {
  display: flex;
  gap: 10px;
  flex: 1;
}

.code-input-group .el-input {
  flex: 1;
}

.code-button {
  min-width: 120px;
  border-radius: 6px;
}

/* 输入框标签样式 */
.input-with-label {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
}

.input-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  min-width: 45px;
  flex-shrink: 0;
  line-height: 1.4;
}

.input-with-label .el-input {
  flex: 1;
}

.login-button {
  width: 100%;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  background: #409eff;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.login-button:hover {
  background: #66b1ff;
}

.login-button i {
  font-size: 16px;
}

.login-tips {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px 16px;
  margin: 16px 0;
  border-left: 4px solid #409eff;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.tip-item:last-child {
  margin-bottom: 0;
}

.tip-item i {
  color: #409eff;
  font-size: 14px;
  flex-shrink: 0;
}

.login-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0 0 0;
}

.switch-button {
  color: #409eff;
  font-weight: 500;
}

.back-button {
  color: #666;
  font-size: 14px;
}

.back-button:hover {
  color: #409eff;
}

.ripple-effect {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transform: scale(0);
  animation: ripple 0.6s linear;
  pointer-events: none;
}

@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 0;
  }
}


/* 响应式设计 */
@media (max-width: 480px) {
  .login-card {
    padding: 25px 20px;
    margin: 10px;
  }
  
  .login-title {
    font-size: 24px;
  }
  
  .code-input-group {
    flex-direction: column;
  }
  
  .code-button {
    min-width: auto;
  }
}
</style>
